import * as React from 'react';
import styled from 'styled-components';
import { getConfirmationStatus, EboxConfirmationStatus } from '../api/publicEbox';

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'found'; data: EboxConfirmationStatus }
  | { kind: 'notFound' }
  | { kind: 'error' };

interface Props {
  /** The project's saved EBox confirmation number (camelCased from the API). */
  confirmationNumber?: string | null;
}

/**
 * Read-only EBox status for the Permit Details view. Calls the public
 * Confirmation-Status API on mount (and whenever the number changes) to show
 * whether the EBox is live and a link to it when live. Renders
 * "Ebox not Assigned" when the project has no number.
 */
const EboxStatus = ({ confirmationNumber }: Props) => {
  const [state, setState] = React.useState<State>({ kind: 'idle' });

  React.useEffect(() => {
    const number = (confirmationNumber || '').trim();
    if (!number) {
      setState({ kind: 'idle' });
      return undefined;
    }

    let cancelled = false;
    setState({ kind: 'loading' });

    getConfirmationStatus(number)
      .then(({ data }) => {
        if (!cancelled) setState({ kind: 'found', data });
      })
      .catch(err => {
        if (cancelled) return;
        setState(err?.response?.status === 404 ? { kind: 'notFound' } : { kind: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [confirmationNumber]);

  const data = state.kind === 'found' ? state.data : null;
  const isLive = !!data?.Live;

  return (
    <Wrapper>
      <Title>Ebox Number</Title>

      {!confirmationNumber && <NotAssigned>Ebox not Assigned</NotAssigned>}

      {state.kind === 'loading' && <Muted>Checking EBox…</Muted>}

      {state.kind === 'notFound' && <Muted>Unknown confirmation number ({confirmationNumber}).</Muted>}

      {state.kind === 'error' && <Muted>Couldn’t reach the EBox service.</Muted>}

      {data && (
        <>
          <Number>{data.ConfirmationNumber}</Number>
          <Row>
            <LiveBadge $live={isLive}>{isLive ? 'Live' : 'Not live'}</LiveBadge>
            {isLive && data.PublicUrl && data.PublicUrl.startsWith('https://') ? (
              <OpenLink href={data.PublicUrl} target="_blank" rel="noreferrer">
                Open EBox ↗
              </OpenLink>
            ) : null}
          </Row>
        </>
      )}

    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-md);
  background: var(--neutral-0);
  box-sizing: border-box;
  text-align: left;
`;

const Title = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 8px;
`;

const NotAssigned = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
  font-style: italic;
`;

const Muted = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const Number = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const LiveBadge = styled.span<{ $live: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ $live }) => ($live ? 'var(--success-50)' : 'var(--danger-50)')};
  color: ${({ $live }) => ($live ? 'var(--success-700)' : 'var(--danger-500)')};
  border: 1px solid ${({ $live }) => ($live ? 'var(--success-500)' : 'var(--danger-500)')};
`;

const OpenLink = styled.a`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--primary-700);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export default EboxStatus;
